import React from "react";
import { Trans, useTranslation } from "react-i18next";
import { FileText, Info, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";

/**
 * Prominent notice explaining Dr. Kichian requires a referral from the patient's
 * family physician. Patients cannot self-refer.
 */
export function ReferralRequiredNotice({ variant = "banner", className = "" } = {}) {
    const { t } = useTranslation();

    if (variant === "compact") {
        return (
            <div
                data-testid="referral-required-notice"
                className={`rounded-2xl border border-[color:var(--brand-teal-2)]/25 bg-white p-4 flex items-start gap-3 ${className}`}
                style={{
                    background:
                        "linear-gradient(135deg, rgba(14,124,134,0.08) 0%, rgba(255,255,255,1) 60%)",
                }}
            >
                <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-[color:var(--brand-teal-soft)] text-[color:var(--brand-teal-2)] shrink-0">
                    <FileText className="h-4 w-4" />
                </span>
                <div className="min-w-0">
                    <p className="text-sm font-semibold text-[color:var(--brand-navy)]">
                        {t("referralNotice.compactTitle")}
                    </p>
                    <p className="mt-1 text-xs text-[color:var(--text-muted)] leading-relaxed">
                        {t("referralNotice.compactBody")}
                    </p>
                </div>
            </div>
        );
    }

    return (
        <section className={`container-page ${className}`} data-testid="referral-required-notice">
            <div
                className="relative overflow-hidden rounded-3xl border border-[color:var(--brand-teal-2)]/30 p-6 sm:p-8 grain"
                style={{
                    background:
                        "linear-gradient(135deg, rgba(14,124,134,0.14) 0%, rgba(255,255,255,1) 65%)",
                }}
            >
                <div className="flex items-start gap-4">
                    <span className="inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-white text-[color:var(--brand-teal-2)] border border-[color:var(--brand-teal-2)]/25 shrink-0">
                        <FileText className="h-5 w-5" />
                    </span>
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                            <Info className="h-4 w-4 text-[color:var(--brand-teal-2)]" />
                            <p className="text-[11px] uppercase tracking-wider text-[color:var(--brand-teal-2)] font-semibold">
                                {t("referralNotice.important")}
                            </p>
                        </div>
                        <h3 className="font-display text-xl sm:text-2xl font-semibold text-[color:var(--brand-navy)]">
                            {t("referralNotice.bannerTitle")}
                        </h3>
                        <p className="mt-2 text-[color:var(--text-muted)] leading-relaxed">
                            <Trans
                                i18nKey="referralNotice.bannerBody"
                                components={{
                                    1: <strong className="text-[color:var(--brand-navy)]" />,
                                    3: <strong className="text-[color:var(--brand-navy)]" />,
                                }}
                            />
                        </p>

                        <div className="mt-4 grid gap-2 sm:grid-cols-3 max-w-xl">
                            {[1, 2, 3].map((n) => (
                                <div key={n} className="rounded-xl border border-[color:var(--border-default)] bg-white px-3 py-2">
                                    <p className="text-[10px] uppercase tracking-wider text-[color:var(--text-soft)]">
                                        {t("referralNotice.step")} {n}
                                    </p>
                                    <p className="text-xs font-semibold text-[color:var(--brand-navy)] leading-tight mt-0.5">
                                        {t(`referralNotice.step${n}`)}
                                    </p>
                                </div>
                            ))}
                        </div>

                        <div className="mt-5 flex flex-wrap items-center gap-3 text-sm">
                            <Link
                                to="/referrals"
                                className="ml-auto inline-flex items-center gap-1 text-sm font-semibold text-[color:var(--brand-navy)] hover:gap-2 transition-[gap]"
                                data-testid="referral-info-link"
                            >
                                {t("referralNotice.infoForReferring")}
                                <ArrowRight className="h-4 w-4" />
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
